import { useEffect, useRef, useState } from 'react';
import { i18nService } from '../services/i18n';
import type { MemoryFileSnapshot } from '../../shared/memoryFile';

/** 记忆编辑的异步状态边界；所有操作共用互斥与代次检查，组件只负责交互展示。 */
export function useMemoryFileEditor(id: string, scope: 'secretary' | 'global' | 'soul', onSaved: (id: string) => void) {
  const [snapshot, setSnapshot] = useState<MemoryFileSnapshot | null>(null);
  const [draft, setDraft] = useState('');
  const [revision, setRevision] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const mutex = useRef(false);
  const generation = useRef(0);
  const changed = snapshot !== null && draft !== snapshot.content;

  /** 请求失败保留草稿；切换文件、卸载或 StrictMode 重挂载后忽略旧响应。 */
  async function request(operation: () => Promise<MemoryFileSnapshot | undefined>, apply: (value: MemoryFileSnapshot) => void) {
    if (mutex.current) return;
    const sequence = ++generation.current;
    mutex.current = true; setBusy(true); setError('');
    try {
      const result = await operation();
      if (!result) throw new Error(i18nService.t('memoryFile_unavailable'));
      if (alive.current && sequence === generation.current) apply(result);
    } catch (cause) {
      if (alive.current && sequence === generation.current) setError(cause instanceof Error ? cause.message : i18nService.t('memoryFile_unavailable'));
    } finally {
      if (sequence === generation.current) { mutex.current = false; if (alive.current) setBusy(false); }
    }
  }

  /** 接受已成功持久化的服务端快照；重命名后的后续操作使用新 ID。 */
  function accept(value: MemoryFileSnapshot) {
    setSnapshot(value); setDraft(value.content); setRevision(''); setPreview(null);
  }

  /** 历史预览不替换草稿；恢复和保存继续比较当前版本摘要。 */
  async function load(revisionId = '', fileId = snapshot?.id ?? id) {
    await request(() => window.electron.cowork.getMemoryFile?.(scope, fileId, revisionId || undefined) ?? Promise.resolve(undefined), value => {
      if (revisionId) { setRevision(revisionId); setPreview(value.revisionContent ?? null); }
      else accept(value);
    });
  }

  useEffect(() => {
    alive.current = true;
    setSnapshot(null); setDraft(''); setRevision(''); setPreview(null);
    void load('', id);
    return () => { alive.current = false; generation.current++; mutex.current = false; };
  }, [id, scope]);

  /** 恢复由服务端修订 ID 定位，浏览器不上传历史全文冒充已有版本。 */
  async function save(restore: boolean) {
    if (!snapshot || (restore && !revision)) return;
    await request(() => window.electron.cowork.editMemoryFile?.(scope, snapshot.id, {
      expectedSha256: snapshot.sha256, ...(restore ? { revisionId: revision } : { content: draft }),
    }) ?? Promise.resolve(undefined), value => { accept(value); onSaved(value.id); });
  }

  /** 未保存草稿不能通过重命名被隐式丢弃，也不发起第二个并发写请求。 */
  async function rename(newId: string) {
    if (scope === 'soul' || !snapshot || changed || revision || newId === snapshot.id) return;
    await request(() => window.electron.cowork.renameMemoryFile?.(scope, snapshot.id, {
      newId, expectedSha256: snapshot.sha256,
    }) ?? Promise.resolve(undefined), value => { accept(value); onSaved(value.id); });
  }

  return { snapshot, draft, setDraft, revision, preview, busy, error, changed, load, save, rename,
    clearPreview: () => { setRevision(''); setPreview(null); } };
}
